/**
 * Размеры от выделенного предмета — пока зажат Ctrl/Cmd, как в Figma:
 *
 *   • выделил предмет и зажал Cmd — расстояния от него до стен комнаты;
 *   • навёл при этом на другой предмет — у стороны, что смотрит на него, вместо размера до
 *     стены — размер до этого предмета; остальные стороны по-прежнему меряются до стен.
 *
 * Работает для мебели на полу, навесного (шкаф, тумба, телевизор, розетка) и светильников
 * на потолке. Как считаются — `itemClearances` / `wallFixtureClearances` (до стен) и
 * `gapClearances` (между предметами) в `widgets/plan/clearances.ts`.
 *
 * Рисуются не как размеры чертежа, а подсказкой поверх: синяя линия, цифра в плашке,
 * выноска к краю второго предмета — пунктиром, сам второй предмет обведён рамкой.
 * На лист PDF не попадают.
 */
import { getCatalogItem } from "@/shared/catalog";
import { formatLength, type Pt } from "@/shared/lib/geometry";
import { useLengthUnit } from "@/shared/store";
import { itemDimensions, type Fixture, type FloorItem, type WallGraph } from "@/shared/plan";
import {
	floorItemFootprint,
	gapClearances,
	itemClearances,
	wallFixtureClearances,
	wallFixtureFootprint,
	type Clearance,
	type Room,
	type WallOutline,
} from "../../plan";

const COLOR = "#2f6feb";

/** Что меряем: предмет на полу или светильник на потолке (`item`) либо навесной. */
export type ClearanceTarget =
	| { kind: "item"; item: FloorItem }
	| { kind: "fixture"; fixture: Fixture };

interface Props {
	/** Выделенный предмет — от него меряем */
	target: ClearanceTarget;
	/** Предмет под курсором — тогда меряем до него, а не до стен */
	other: ClearanceTarget | null;
	graph: WallGraph;
	rooms: Room[];
	outlines: Record<string, WallOutline>;
	mmPerPx: number;
}

/** Контур предмета на плане; null — не найден в каталоге или стена пропала. */
const footprintOf = (target: ClearanceTarget, graph: WallGraph): Pt[] | null => {
	const self = target.kind === "item" ? target.item : target.fixture;
	const info = getCatalogItem(self.ref);
	if (!info) return null;
	const size = itemDimensions(self, info);
	return target.kind === "item"
		? floorItemFootprint(target.item, size)
		: wallFixtureFootprint(graph, target.fixture, size);
};

/** Размеры смотрят в одну сторону — от одной стороны предмета. */
const sameWay = (a: Clearance, b: Clearance) => {
	const ax = a.to.x - a.from.x;
	const ay = a.to.y - a.from.y;
	const bx = b.to.x - b.from.x;
	const by = b.to.y - b.from.y;
	return (ax * bx + ay * by) / (Math.hypot(ax, ay) * Math.hypot(bx, by)) > 0.99;
};

/** Размеры от предмета до стен. */
const wallClearances = (
	target: ClearanceTarget,
	graph: WallGraph,
	rooms: Room[],
	outlines: Record<string, WallOutline>,
): Clearance[] => {
	const self = target.kind === "item" ? target.item : target.fixture;
	const info = getCatalogItem(self.ref);
	if (!info) return [];
	const size = itemDimensions(self, info);
	return target.kind === "item"
		? itemClearances(target.item, size, rooms, outlines)
		: wallFixtureClearances(graph, target.fixture, size, rooms, outlines);
};

export const Clearances2D = ({ target, other, graph, rooms, outlines, mmPerPx }: Props) => {
	const px = (n: number) => n * mmPerPx;
	let clearances: Clearance[];
	/** Контур предмета под курсором — его обводим рамкой, как в Figma */
	let them: Pt[] | null = null;
	clearances = wallClearances(target, graph, rooms, outlines);
	if (other) {
		const self = footprintOf(target, graph);
		them = footprintOf(other, graph);
		const gaps = self && them ? gapClearances(self, them) : [];
		// Сторона, что смотрит на предмет под курсором, меряется до него, а не до стены
		clearances = [...clearances.filter((wall) => !gaps.some((gap) => sameWay(wall, gap))), ...gaps];
	}
	return (
		<g data-plan-export-exclude style={{ pointerEvents: "none" }}>
			{them && (
				<polygon
					points={them.map((p) => `${p.x},${p.y}`).join(" ")}
					fill={COLOR}
					fillOpacity={0.06}
					stroke={COLOR}
					strokeWidth={px(1.5)}
				/>
			)}
			{clearances.map((clearance) => (
				<ClearanceLine
					key={`${Math.round(clearance.from.x)}:${Math.round(clearance.from.y)}:${Math.round(clearance.to.x)}:${Math.round(clearance.to.y)}`}
					clearance={clearance}
					mmPerPx={mmPerPx}
				/>
			))}
		</g>
	);
};

/** Одна линия: засечки на концах, длина в плашке посередине и, если есть, выноска пунктиром. */
export const ClearanceLine = ({
	clearance,
	mmPerPx,
}: {
	clearance: Clearance;
	mmPerPx: number;
}) => {
	const unit = useLengthUnit();
	const px = (n: number) => n * mmPerPx;
	const { from, to, guide } = clearance;
	const dx = to.x - from.x;
	const dy = to.y - from.y;
	const length = Math.hypot(dx, dy);
	const side = { x: -dy / length, y: dx / length };
	const tick = (p: Pt) => (
		<line
			x1={p.x - side.x * px(5)}
			y1={p.y - side.y * px(5)}
			x2={p.x + side.x * px(5)}
			y2={p.y + side.y * px(5)}
			stroke={COLOR}
			strokeWidth={px(1.4)}
		/>
	);
	const label = formatLength(length, unit);
	const fontSize = px(11);
	const boxWidth = label.length * fontSize * 0.6 + px(10);
	const boxHeight = fontSize + px(6);
	const mid = { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 };
	return (
		<g>
			{guide && (
				<line
					x1={guide[0].x}
					y1={guide[0].y}
					x2={guide[1].x}
					y2={guide[1].y}
					stroke={COLOR}
					strokeWidth={px(1)}
					strokeDasharray={`${px(4)} ${px(3)}`}
				/>
			)}
			<line x1={from.x} y1={from.y} x2={to.x} y2={to.y} stroke={COLOR} strokeWidth={px(1.2)} />
			{tick(from)}
			{tick(to)}
			<rect
				x={mid.x - boxWidth / 2}
				y={mid.y - boxHeight / 2}
				width={boxWidth}
				height={boxHeight}
				rx={px(4)}
				fill="#ffffff"
				stroke={COLOR}
				strokeWidth={px(1)}
			/>
			<text
				x={mid.x}
				y={mid.y}
				textAnchor="middle"
				dominantBaseline="central"
				fontSize={fontSize}
				fontWeight={600}
				fill={COLOR}
				style={{ userSelect: "none" }}
			>
				{label}
			</text>
		</g>
	);
};
